// Hạt (particles) cho khung avatar — TỐI ƯU: MỘT vòng requestAnimationFrame duy nhất
// cho tất cả canvas đang bật (thay vì mỗi avatar 1 vòng). Canvas nào ra khỏi màn hình
// thì tự huỷ đăng ký (caller lo qua IntersectionObserver). Tôn trọng prefers-reduced-motion.

export type ParticleType = 'ember' | 'dragon' | 'void';

interface Particle {
  x: number;
  y: number;
  size: number;
  alpha: number;
  speedY: number;
  speedX: number;
  angle: number;
  radius: number;
  speed: number;
  color: string;
  yOffset: number;
}

interface Entry {
  ctx: CanvasRenderingContext2D;
  type: ParticleType;
  color: string;
  particles: Particle[];
  w: number;
  h: number;
  center: number;
}

const CANVAS = 200; // độ phân giải nội tại; CSS sẽ scale theo cỡ avatar
const CENTER = 100;

const registry = new Map<HTMLCanvasElement, Entry>();
let rafId: number | null = null;

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const makeParticles = (type: ParticleType, color: string, density: number): Particle[] => {
  const out: Particle[] = [];
  for (let i = 0; i < density; i += 1) {
    if (type === 'ember') {
      out.push({
        x: Math.random() * CANVAS,
        y: Math.random() * CANVAS,
        size: Math.random() * 1.6 + 0.6,
        alpha: Math.random() * 0.8 + 0.2,
        speedY: Math.random() * 0.45 + 0.2,
        speedX: (Math.random() - 0.5) * 0.4,
        angle: 0, radius: 0, speed: 0, color, yOffset: 0,
      });
    } else if (type === 'dragon') {
      out.push({
        angle: Math.random() * Math.PI * 2,
        radius: Math.random() * 28 + 44,
        size: Math.random() * 1.7 + 0.6,
        speed: Math.random() * 0.02 + 0.008,
        color: Math.random() > 0.4 ? '#fef08a' : '#f59e0b',
        alpha: Math.random() * 0.8 + 0.2,
        yOffset: 0, x: 0, y: 0, speedY: 0, speedX: 0,
      });
    } else {
      out.push({
        angle: Math.random() * Math.PI * 2,
        radius: Math.random() * 32 + 46,
        size: Math.random() * 1.7 + 0.6,
        speed: Math.random() * 0.03 + 0.012,
        color: Math.random() > 0.5 ? '#ef4444' : '#c084fc',
        alpha: Math.random() * 0.8 + 0.2,
        yOffset: 0, x: 0, y: 0, speedY: 0, speedX: 0,
      });
    }
  }
  return out;
};

const draw = (e: Entry) => {
  const { ctx, type, color, particles } = e;
  ctx.clearRect(0, 0, CANVAS, CANVAS);
  for (const p of particles) {
    if (type === 'ember') {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = p.alpha;
      ctx.shadowBlur = 6;
      ctx.shadowColor = color;
      ctx.fill();
      p.y -= p.speedY;
      p.x += Math.sin(p.y * 0.05) * p.speedX;
      if (p.y < 6) { p.y = CANVAS - 14; p.x = Math.random() * CANVAS; }
    } else if (type === 'dragon') {
      p.angle += p.speed;
      p.yOffset -= 0.32;
      const x = CENTER + Math.cos(p.angle) * p.radius;
      const y = CENTER + Math.sin(p.angle) * p.radius + p.yOffset;
      ctx.beginPath();
      ctx.arc(x, y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.alpha;
      ctx.shadowBlur = 8;
      ctx.shadowColor = p.color;
      ctx.fill();
      if (y < CENTER - 72) { p.yOffset = 22; p.alpha = Math.random() * 0.8 + 0.2; }
    } else {
      p.angle += p.speed;
      p.radius -= 0.12;
      const x = CENTER + Math.cos(p.angle) * p.radius;
      const y = CENTER + Math.sin(p.angle) * p.radius;
      ctx.beginPath();
      ctx.arc(x, y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.alpha;
      ctx.shadowBlur = 9;
      ctx.shadowColor = p.color;
      ctx.fill();
      if (p.radius < 30) { p.radius = Math.random() * 22 + 64; }
    }
  }
};

const tick = () => {
  registry.forEach(draw);
  rafId = registry.size ? requestAnimationFrame(tick) : null;
};

export const registerParticles = (
  canvas: HTMLCanvasElement,
  type: ParticleType,
  color: string,
  density: number
): void => {
  if (registry.has(canvas)) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  canvas.width = CANVAS;
  canvas.height = CANVAS;
  registry.set(canvas, {
    ctx, type, color, w: CANVAS, h: CANVAS, center: CENTER,
    particles: makeParticles(type, color, density),
  });
  if (rafId === null) rafId = requestAnimationFrame(tick);
};

export const unregisterParticles = (canvas: HTMLCanvasElement): void => {
  registry.delete(canvas);
  if (registry.size === 0 && rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};
